import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

// Cache pour les avatars avec expiration
const AVATAR_CACHE_KEY = 'userAvatarCache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures en millisecondes

// Fonctions de gestion du cache
const getCachedAvatar = (userId) => {
  try {
    const cache = localStorage.getItem(AVATAR_CACHE_KEY);
    if (!cache) return null;
    
    const avatarCache = JSON.parse(cache);
    const userCache = avatarCache[userId];
    
    if (!userCache) return null;
    
    // Vérifier si le cache n'a pas expiré
    const now = Date.now();
    if (now > userCache.expiresAt) {
      // Supprimer l'entrée expirée
      delete avatarCache[userId];
      localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(avatarCache));
      return null;
    }
    
    return userCache;
  } catch (error) {
    console.error('Erreur lors de la lecture du cache avatar:', error);
    return null;
  }
};

const setCachedAvatar = (userId, optimizedUrl, fallbackInitials) => {
  try {
    const cache = localStorage.getItem(AVATAR_CACHE_KEY);
    const avatarCache = cache ? JSON.parse(cache) : {};
    
    avatarCache[userId] = {
      optimizedUrl,
      fallbackInitials,
      cachedAt: Date.now(),
      expiresAt: Date.now() + CACHE_DURATION
    };
    
    localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(avatarCache));
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du cache avatar:', error);
  }
};

const UserAvatar = ({ 
  user, 
  className = "", 
  size = "h-8 w-8",
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [cachedData, setCachedData] = useState(null);
  const maxRetries = 2;

  // Obtenir les initiales pour l'avatar (mémorisé pour éviter recalculs)
  const getInitials = useMemo(() => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
    }
    return user?.email?.[0].toUpperCase() || 'U';
  }, [user?.displayName, user?.email]);

  // Charger les données du cache au montage et quand l'utilisateur change
  useEffect(() => {
    if (user?.uid) {
      const cached = getCachedAvatar(user.uid);
      setCachedData(cached);
      setImageError(false);
      setRetryCount(0);
    } else {
      setCachedData(null);
    }
  }, [user?.uid]);

  // Gérer les erreurs de chargement d'image
  const handleImageError = useCallback(() => {
    if (retryCount < maxRetries) {
      // Retry avec un délai progressif
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageError(false);
      }, Math.pow(2, retryCount) * 1000); // 1s, 2s, 4s...
    } else {
      setImageError(true);
    }
  }, [retryCount, maxRetries]);

  // Gérer le succès du chargement d'image
  const handleImageLoad = useCallback(() => {
    if (user?.uid && !imageError) {
      const optimizedUrl = getOptimizedPhotoURL(user?.photoURL);
      if (optimizedUrl) {
        // Sauvegarder dans le cache après un chargement réussi
        setCachedAvatar(user.uid, optimizedUrl, getInitials);
      }
    }
  }, [user?.uid, user?.photoURL, imageError, getInitials]);

  // Optimiser l'URL de l'image Google
  const getOptimizedPhotoURL = useCallback((url) => {
    if (!url) return null;
    
    // Pour les images Google, ajouter des paramètres d'optimisation
    if (url.includes('googleusercontent.com')) {
      // Retirer les anciens paramètres de taille et ajouter les nouveaux
      const baseUrl = url.split('=')[0];
      return `${baseUrl}=s96-c-rp-mo-br100`; // s96 (size), c (crop), rp (round profile), mo (modern), br100 (border radius)
    }
    
    return url;
  }, []);

  // Déterminer quelle URL utiliser (cache prioritaire, puis URL optimisée)
  const avatarUrl = useMemo(() => {
    // PRIORITÉ 1: Utiliser l'URL en cache si disponible (PAS de requête vers Google)
    if (cachedData?.optimizedUrl) {
      return cachedData.optimizedUrl;
    }
    
    // PRIORITÉ 2: En cas d'erreur répétée, ne pas essayer de charger l'image
    if (imageError && retryCount >= maxRetries) {
      return null;
    }
    
    // PRIORITÉ 3: Pas de cache → faire une requête vers Google
    return getOptimizedPhotoURL(user?.photoURL);
  }, [cachedData, imageError, retryCount, maxRetries, user?.photoURL, getOptimizedPhotoURL]);

  // Déterminer les initiales à utiliser (cache prioritaire)
  const displayInitials = useMemo(() => {
    // Utiliser les initiales en cache si disponibles
    if (cachedData?.fallbackInitials) {
      return cachedData.fallbackInitials;
    }
    
    // Sinon, calculer les initiales actuelles
    return getInitials;
  }, [cachedData, getInitials]);

  return (
    <Avatar className={`${size} ${className}`} {...props}>
      {avatarUrl && (
        <AvatarImage 
          src={avatarUrl}
          alt={user?.displayName || 'User'} 
          // Seulement gérer les erreurs et le chargement si ce n'est PAS un avatar en cache
          onError={cachedData?.optimizedUrl ? undefined : handleImageError}
          onLoad={cachedData?.optimizedUrl ? undefined : handleImageLoad}
          // Optimisations pour les images externes
          loading="lazy"
          decoding="async"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
      )}
      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold">
        {displayInitials}
      </AvatarFallback>
    </Avatar>
  );
};

// Fonction utilitaire pour nettoyer les avatars expirés du cache
export const cleanupExpiredAvatarCache = () => {
  try {
    const cache = localStorage.getItem(AVATAR_CACHE_KEY);
    if (!cache) return;
    
    const avatarCache = JSON.parse(cache);
    const now = Date.now();
    let cleaned = false;
    
    // Supprimer les entrées expirées
    Object.keys(avatarCache).forEach(userId => {
      if (now > avatarCache[userId].expiresAt) {
        delete avatarCache[userId];
        cleaned = true;
      }
    });
    
    if (cleaned) {
      localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(avatarCache));
    }
  } catch (error) {
    console.error('Erreur lors du nettoyage du cache avatar:', error);
  }
};

// Nettoyer automatiquement au chargement (exécuté une seule fois)
if (typeof window !== 'undefined') {
  // Attendre que la page soit chargée avant de nettoyer
  setTimeout(cleanupExpiredAvatarCache, 1000);
}

export default UserAvatar; 